/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { ButtonStyle, ButtonSize } from 'src/aero/aero-button/aero-button.component';
import { ClipboardService } from 'ngx-clipboard';
import { Component, Inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { ResumeService } from '../../resume/resume.service';

/**
 * Visual component for the pop-up modal to show the share link for a resume
 */
@Component({
  selector: 'bulb-share-link-dialog',
  templateUrl: './share-link-dialog.component.html',
  standalone: false,
})
export class ShareLinkDialogComponent {
  /**
   * Reference to current user.
   */
  public account: any;

  /**
   * Indicates whether the Resume Share modal is active.
   * if false, the Portfolio Share modal is shown.
   */
  public showResumeShareModal: boolean;

  /**
   * Reference to resume tokenId.
   */
  public token : string;

  /**
   * buttonSize of aero button
   */
  public buttonSize: ButtonSize = ButtonSize.LARGER;

  /**
   * buttonStyle of aero button
   */
  public buttonStyle: ButtonStyle = ButtonStyle.TEXT_WITH_ICON;

  /**
   * Default constructor
   *
   * @param bulbBusyCoverService
   * @param bulbModalService
   * @param clipboardService
   * @param dialogConfig
   * @param dynamicDialogRef
   * @param AppConfigService
   * @param ResumeService
   * @param http
   */
  constructor(
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    @Inject('BulbModalService') private bulbModalService,
    private aeroDialogService: AeroDialogService,
    private AppConfigService: AppConfigService,
    private clipboardService: ClipboardService,
    private dialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private ResumeService: ResumeService,
    private http: HttpClient,
  ) {
    // @ts-ignore
    this.account = this.dialogConfig.account;
    this.showResumeShareModal = this.dialogConfig.data.showResumeShareModal;

    this.bulbBusyCoverService.showBusyDuring(
      this.fetchAccountToken().then((resumeToken) => {
        this.token = resumeToken.token;
      }),
    );
  }

  /**
   * Get the resume or portfolio share link composed of the url the instance is running on,
   * the account name and the token.
   */
  getShareLink() {
    const path: string = this.showResumeShareModal ? 'resume' : 'portfolio';
    return `${this.AppConfigService.config.base.url}/${this.account.username}/${path}?token=${this.token}`;
  }

  /**
   * Close reference to resume contact dialog.
   */
  closeResumeDialog(): void {
    this.dynamicDialogRef.close();
  }

  /**
   * Regenerate the share link and store the new token within it's corresponding variable
   */
  regenerateShareLink() {
    this.closeResumeDialog();
    const bodyTitleCode = this.showResumeShareModal
      ? 'resume_confirm_regenerate_link_body' : 'account_confirm_regenerate_link_body';
    const modalOptions = {
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      titleCode: 'resume_confirm_regenerate_link_title',
      bodyCode: bodyTitleCode,
      okButtonCode: 'button_confirm',
      cancelButtonCode: 'button_cancel',
      showCancelButton: true,
      windowClass: 'bulb-aero-modal updated-modal-design bulb-aero-modal-buttons bulb-aero-modal-buttons-primary-dismissal',
    };

    this.bulbModalService.show(modalOptions).then(() => {
      this.bulbBusyCoverService.showBusyDuring(
        this.regenerateAccountToken().then((resumeToken) => {
          this.token = resumeToken.token;
        }).finally(() => {
          this.aeroDialogService.showShareLinkDialog(this.account, false, this.showResumeShareModal);
        }),
      );
    });
  }

  /**
   * Copy the share link to the clipboard and then show the copied text on the screen
   */
  copyShareLink() {
    this.clipboardService.copy(this.getShareLink());

    const $copyText = jQuery('.successful-copy');
    $copyText.addClass('show-copy-text');
    const $copyShareableLinkButton = jQuery('.resume-clipboard-container');
    $copyShareableLinkButton.focus();
    setTimeout(() => {
      $copyText.removeClass('show-copy-text');
    }, 7000);
  }

  /**
   * Fetch token associated with the portfolio or resume.
   */
  fetchAccountToken() : Promise < any > {
    return this.showResumeShareModal ? this.ResumeService.fetchResumeToken(this.account.resumeId) : this.http.get(`accounts/${this.account.id}/token`).toPromise();
  }

  /**
   * Generate the portfolio token
   */
  regenerateAccountToken() : Promise < any > {
    return this.showResumeShareModal ? this.ResumeService.regenerateResumeToken(this.account.resumeId) : this.http.post(`accounts/${this.account.id}/token`, {}).toPromise();
  }
}
